<template>
  <section class="order-summary">
    <div class="section-title">
      <h2>{{ getLangString('orderSummaryTitle') }}</h2>
    </div>
    <b-container class="order-summary-container section-card">
      <b-row>
        <b-col>
          <div class="order-body">
            <div class="order-type order-field">
              <div class="title">
                Wine Club
              </div>
              <div class="value">
                {{ getSelectedOrderTypeObject.title }}
              </div>
            </div>
            <div class="order-frequency order-field">
              <div class="title">
                Delivery Frequency
              </div>
              <div class="value">
                {{ selectedFrequency }}
              </div>
            </div>
            <div class="order-quantity order-field">
              <div class="title-container">
                <div class="title">
                  Quantity
                </div>
                <div class="value">
                  {{ selectedQuantity }}
                </div>
              </div>
              <div
                v-if="isCustomOrder"
                class="products"
              >
                <div
                  v-for="(product, index) in productList"
                  :key="index"
                  class="product"
                >
                  <div class="product-image">
                    <AImage
                      :img="product.image"
                      preset="wineproductteaser"
                      responsive
                      contain
                    />
                    <div class="product-quantity">
                      {{ product.quantity }}
                    </div>
                  </div>
                  <div class="product-title">
                    {{ product.title | formatEntities }}
                  </div>
                  <div class="product-price">
                    {{ product.price }}
                  </div>
                </div>
                <div
                  v-if="containsInvalidProducts"
                  class="product-message"
                >
                  <span class="product-notice">Note:</span> This order contains invalid or missing products. Please update this order as soon as possible, as it cannot be processed until this error is resolved.
                </div>
              </div>
            </div>
            <div class="subtotal">
              <div class="subtotal-title">
                Subtotal
              </div>
              <div class="subtotal-price">
                {{ subtotal }}
              </div>
            </div>
            <div
              v-if="discount && showRrp"
              class="discount"
            >
              <div class="discount-title">
                Discount
              </div>
              <div class="discount-price">
                {{ discount }}
              </div>
            </div>
            <div
              v-if="shipping"
              class="shipping"
            >
              <div class="shipping-title">
                Shipping
              </div>
              <div
                v-if="shippingCalculated"
                class="shipping-price"
              >
                {{ shipping }}
              </div>
              <div
                v-else
                class="shipping-price"
              >
                {{ getLangString('shippingCalculating') }}
              </div>
            </div>
            <div class="total-container">
              <div class="order-actions">
                <b-btn
                  v-if="!orderLocked"
                  variant="outline-primary"
                  class="change-order-button"
                  @click="changeOrder"
                >
                  {{ getLangString('changeOrderButton') }}
                </b-btn>
                <b-btn
                  v-if="!orderLocked"
                  variant="outline-primary"
                  class="clear-order-button"
                  @click="clearOrder"
                >
                  {{ getLangString('clearOrderButton') }}
                </b-btn>
                <b-btn
                  v-if="orderId"
                  variant="outline-primary"
                  class="cancel-button"
                  @click="cancelOrder"
                >
                  {{ getLangString('cancelOrderButton') }}
                </b-btn>
              </div>
              <div class="total">
                <div class="total-title">
                  Total
                </div>
                <div
                  v-if="shippingCalculated"
                  class="total-price"
                >
                  {{ total }}
                </div>
                <div
                  v-else
                  class="total-calculating"
                >
                  {{ getLangString('shippingCalculating') }}
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </section>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'

export default {
  name: 'order-summary',
  data() {
    return {
      prevPostcode: ''
    }
  },
  computed: {
    ...mapState('somnambulistui', ['selectedProducts', 'orderId', 'orderLocked', 'pickup', 'shippingAddress', 'shippingCalculated', 'shippingCost']),
    ...mapGetters('somnambulistui', ['isCustomOrder', 'getSelectedOrderTypeObject', 'getSelectedOrderProducts', 'getSelectedQuantityObject', 'getTotalString', 'getRrpString', 'getDiscountString', 'getFallbackSubtotalString', 'getLangString', 'showRrp']),
    selectedFrequency() {
      let freq = this.$store.state.somnambulistui.selectedFrequency
      return this.$store.getters['somnambulistui/frequency'](freq)
    },
    selectedQuantity() {
      let qty = this.$store.state.somnambulistui.selectedQuantity
      return this.$store.getters['somnambulistui/quantity'](qty)
    },
    productList() {
      if (this.isCustomOrder) {
        return Object.keys(this.selectedProducts).map((pid) => {
          let product = this.getSelectedOrderProducts.find(product => product.id === pid)
          if (product) {
            return {
              title: product.title,
              quantity: this.selectedProducts[pid],
              price: '$' + (this.selectedProducts[pid] * parseFloat(product.productsettings.price.toString().replace('$', ''))).toFixed(2),
              image: (product.images.length > 0) ? (product.images[0].image || null) : null
            }
          }
        }, this).filter(x => x)
      }
      else {
        return []
      }
    },
    containsInvalidProducts() {
      if (!this.isCustomOrder) return false

      for (const pid of Object.keys(this.selectedProducts)) {
        if (!this.getSelectedOrderProducts.find(product => product.id === pid)) return true
      }

      return false
    },
    subtotal() {
      return (this.getRrpString) ? this.getRrpString : (this.getFallbackSubtotalString) ? this.getFallbackSubtotalString : 'N/A'
    },
    total() {
      return this.getTotalString
    },
    discount() {
      return this.getDiscountString
    },
    shipping() {
      if (this.pickup) {
        return 'Pick up'
      }
      return `$${this.shippingCost.toFixed(2)}`
    }
  },
  watch: {
    shippingAddress: {
      handler: function(newAddress) {
        const postcode = newAddress.postcode === undefined ? newAddress.zip : newAddress.postcode
        if (postcode && postcode.length > 3 && this.getSelectedQuantityObject.shippingType === 'calculo' && postcode !== this.prevPostcode) {
          this.$store.dispatch('somnambulistui/calculateShipping')
        }
        this.prevPostcode = newAddress.postcode || newAddress.zip
      },
      deep: true
    },
    pickup(pickup) {
      this.$store.dispatch('somnambulistui/calculateShipping')
    }
  },
  mounted() {
    this.SET_SHIPPING_CALCULATED(false)
    this.$store.dispatch('somnambulistui/calculateShipping')
  },
  methods: {
    ...mapMutations('somnambulistui', ['SET_ORDER_COMPLETED', 'SET_SHIPPING_CALCULATED']),
    changeOrder() {
      if (!this.orderLocked) {
        this.$store.commit('somnambulistui/SET_DIRTY_FLAG', { key: 'order', value: true })
        this.$store.commit('somnambulistui/SET_UPDATING_ORDER', true)
        this.SET_ORDER_COMPLETED(false)
        this.$store.commit('somnambulistui/SET_ORDER_MODE', 'edit')
      }
    },
    clearOrder() {
      if (!this.orderLocked) {
        this.$store.commit('somnambulistui/SET_DIRTY_FLAG', { key: 'order', value: true })
        this.SET_ORDER_COMPLETED(false)
        this.$store.dispatch('somnambulistui/clearOrderSelection')
        this.$store.commit('somnambulistui/SET_ORDER_MODE', 'edit')
      }
    },
    cancelOrder() {
      this.$router.back()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@scss/custom.scss';

.order-summary {

  .order-summary-container{
    background-color: var(--main-section-alternate-background-color);
  }
    .section-title {
      margin: 0 0 2em 0;
      width: 100%;
      text-align: center;
    }
    padding: 2em 0 3em 0;
    color: var(--main-text-color);

    .subtotal, .discount, .shipping, .total {
      @include media-breakpoint-up(down) {
      .subtotal-price, .discount-price, .shipping-price, .total-price {
        margin-left:auto;
      }
    }

    @include media-breakpoint-up(md) {
      justify-content: flex-end;
    }
  }

  .subtotal {
    padding: 1em 0em 0em 0em;
    display: flex;
    //justify-content: flex-end;
    align-items: center;
    .subtotal-title {
      color: var(--main-text-heading-color);
      font-size: 1.3em;
    }
    .subtotal-price {
      text-align: right;
      min-width: 200px;
    }
  }
  .discount {
    padding: 1em 0em 0em 0em;
    display: flex;
    //justify-content: flex-end;
    align-items: center;
    .discount-title {
      color: var(--main-text-heading-color);
      font-size: 1.3em;
    }
    .discount-price {
      text-align: right;
      min-width: 200px;
    }
  }
  .shipping {
    border-bottom: 1px solid var(--main-border-color);
    padding: 1em 0em 1em 0em;
    display: flex;
    //justify-content: flex-end;
    align-items: center;
    .shipping-title {
      color: var(--main-text-heading-color);
      font-size: 1.3em;
    }
    .shipping-price {
      text-align: right;
      min-width: 200px;
    }
  }
  .total-container {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction:column-reverse;
    padding:1.5em 0;
    @include media-breakpoint-up(md) {
    flex-direction:row;
    }

    .order-actions{
      padding:1em 0;
      @include media-breakpoint-down(md) {
      display: flex;
      flex-grow: 1;
      width: 100%;
      }

      .btn {
        padding:.75em 1em;
        margin-right:1em;
        @include media-breakpoint-down(md) {
        width:100%;
        }
        @include media-breakpoint-down(sm) {
        &:last-child{
        margin-right:0;
        }
        }
      }
    }

    .total {
      margin-left: auto;
      display: flex;
      padding-bottom: 0em;
      align-items: center;
       @include media-breakpoint-down(md) {
        width:100%;
      }

      .total-title {
        color: var(--main-text-heading-color);
        font-size: 1.3em;
      }
      .total-price {
        font-size: 2em;
        font-weight: 600;
        text-align: right;
        min-width: 200px;
      }
      .total-calculating {
        color: var(--main-text-heading-color);
        font-size: 1.5em;
        text-align: right;
        min-width: 200px;
      }
    }
  }
  .order-title {
    margin: 0em 0em 1em 0em;
    text-align: center;
  }
  .order-body {
    .order-field {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding: 1em 0em 1em 0em;
      .title {
        color: var(--main-text-heading-color);
        font-size: 1.3em;
      }
      .value {
        margin-left: auto;
      }
      &:not(:last-child) {
        border-bottom: 1px solid var(--main-border-color);
      }
    }
    .order-quantity {
      flex-direction: column;
      .title-container {
        width: 100%;
        display: flex;
        justify-content: flex-start;
        align-items: center;
      }
    }
  }
}
.products {
  width: 100%;
  margin: 1em 0 1em 0;
  padding: 3em;
  border: 1px solid var(--main-border-color);
  .product {
    &:not(:last-child) {
      border-bottom: 1px solid var(--main-border-color);
      padding-bottom: 2em;
    }
    &:not(:first-child) {
      padding-top: 2em;
    }
    display: flex;
    justify-content: flex-start;
    align-items: center;
    .product-image {
      width: 4em;
      height: 4em;
      margin-right: 2em;
      position: relative;

      .product-quantity {
        position: absolute;
        top: -1rem;
        left: -1rem;
        width: 2rem;
        height: 2rem;
        font-size: 0.8em;
        border-radius: 100%;
        background-color: var(--main-badge-background-color);
        color: var(--main-badge-text-color);
        align-items: center;
        align-content: center;
        display: flex;
        justify-content: center;
        z-index: 2;
      }
    }
    .product-title {
      color: var(--main-text-heading-color);
      font-size: 1.3em;
    }
    .product-price {
      margin-left: auto;
    }
  }
  .product-message {
    font-size: 1em;
    .product-notice {
      font-weight: bold;
      color: red;
    }
  }
}
</style>
